<template>
  <div>
    <div v-if="loading" class="loading-bar"/>
    <div v-else class="woo-tutorial-wrapper">
      <video-playlist v-if="!tutorial.isLite" ref="wooPlaylist"/>
      <div class="woo-tutorial" v-if="tutorial">
        <div v-if="!activeVideo">
          <div class="woo-tutorial-header">
            <v-img
              :src="tutorial.toc_image_url"
              :lazy-src="tutorial.toc_image_url"
              aspect-ratio="1"
              class="woo-tutorial-hero"
            />
            <div class="woo-tutorial-navigation"
              :style="{ color: `#${tutorial.toc_banner_font_icon_color || '000'}`}">
              <v-row>
                <v-col align="left" cols="4" class="left-menu" style="height: 88px;">
                  <router-link to="/">
                    <v-icon class="woo-tutorial-navigation-back" :style="{ color: `#${tutorial.toc_banner_font_icon_color || '000'}` }"> mdi-arrow-left </v-icon>
                  </router-link>
                </v-col>
                <v-col align="right" v-if="tutorial.pattern_download_url">
                  <router-link
                    :style="{
                      color: `#${tutorial.pattern_link_color || '000'}`
                    }"
                    class="download"
                    target="_blank"
                    :to="`/tutorial/${this.$route.params.id}/pattern`" >Download pattern</router-link>
                  <v-icon @click="toggleHelpDrawer" :style="{ color: `#${tutorial.toc_banner_font_icon_color || '000'}` }">
                    mdi-help-circle-outline
                  </v-icon>
                </v-col>
              </v-row>
            </div>
            <div
              class="woo-tutorial-start">
              <v-btn
                v-if="!tutorial.isLite"
                :to="`?v=${startVideo}&s=${startStep}`"
                class="woo-button woo-tutorial-start-button"
                :style="{ background: `#${tutorial.toc_button_color || '000'}`, color: buttonFontColor }"
                elevation="2"
                rounded
                >get started</v-btn
              >
              <div class="woo-tutorial-start-supplement">
                <hand-chooser :tutorial="tutorial" v-if="!tutorial.noHands"/>
              </div>
            </div>
          </div>
          <div class="woo-tutorial-contents">
            <table-of-contents />
          </div>
          <div class="woo-tutorial-disclaimer" v-if="tutorial.legal_disclaimer" v-text="tutorial.legal_disclaimer"/>
        </div>
        <help-drawer ref="helpDrawer"></help-drawer>
      </div>
    </div>
    <page-footer v-if="!activeVideo"/>
  </div>
</template>

<script>
import chroma from 'chroma-js';
import { get } from 'lodash/fp'
import TableOfContents from '@/components/TableOfContents.vue';
import VideoPlaylist from '@/components/VideoPlaylist.vue';
import HandChooser from '@/components/HandChooser.vue';
import EventHub, { Events } from '@/helpers/EventHub';
import HelpDrawer from '@/components/HelpDrawer.vue';
import PageFooter from '@/components/PageFooter.vue';
import Db from '@/helpers/Db';

export default {
  name: 'TutorialView',
  data() {
    return {
      loading: true,
      currentPanel: 0,
      tutorial: null,
    };
  },
  computed: {
    activeVideo() {
      return this.$route.query.v || undefined;
    },
    startStep() {
      if (!this.tutorial || this.tutorial.isLite) return;
      return get('sections.0.steps.0.id', this.tutorial);
    },
    startVideo() {
      if (!this.tutorial || this.tutorial.isLite) return;
      return get('sections.0.steps.0.microsteps.0.id', this.tutorial);
    },
    buttonFontColor() {
      if (!this.tutorial) return;

      const buttonBackground = chroma(this.tutorial.toc_button_color || '#000');
      const labLightness = buttonBackground.get('lab.l');
      const isDarkBackground = labLightness < 70;
      
      // We'll set an arbitrary threshold that will determine if the color
      // is light or black and we can color the font opposite of that
      return isDarkBackground ? 'white' : 'black';
    }
  },
  watch: {},
  components: { TableOfContents, PageFooter, VideoPlaylist, HelpDrawer, HandChooser },
  async mounted() {
    const tutorialToc = await Db.getTutorialBySlug(this.$route.params.id);

    if (!tutorialToc) {
      console.log('tutorialToc not there', this.$route.params.id)
      this.$router.replace('/404');
      return;
    }

    await Db.fetchTutorial(tutorialToc.id)
      .then(tutorial => {
        this.$store.commit('setTutorial', tutorial);
        this.tutorial = tutorial;
        this.loading = false;
      })

    if (this.tutorial.isLite) return;

    if (!this.$refs.wooPlaylist) {
      this.$router.replace('/404');
    } else {
      this.$refs.wooPlaylist.isLoaded = false;
    }

    if (this.$route.query?.dev) {
      this.$store.commit('devModeOn', true);
    }
  },
  methods: {
    toggleHelpDrawer() {
      EventHub.$emit(Events.ShowHelp);
    },
    getStepIcon(step) {
      if (step.completed) {
        return 'mdi-check';
      } else if (step.video) {
        return 'mdi-play-outline';
      } else {
        return 'mdi-text';
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.woo {
  &-tutorial {
    &-disclaimer {
      text-align: left;
      padding-top: 8px;
      padding-left: 12px;
      font-size: 14px;
    }

    &-header {
      position: relative;
    }
    &-navigation {
      padding: 0.5rem 1rem 2rem 2rem;
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      z-index: 3;
      color: black;

      .left-menu {
        align-items: center;
        display: flex;
      }

      .v-icon {
        color: black;
      }
      .download {
        text-decoration: underline;
        padding-right: 0.5rem;
        font-weight: 700;
      }
    }
    &-start {
      position: absolute;
      left: 50%;
      bottom: 1.5rem;
      transform: translateX(-50%);

      &-button {
        color: white;
        height: 48px !important;
      }

      &-supplement {
        margin-top: 0.5rem;
        font-size: 0.875rem;
        font-weight: 700;
        text-decoration: underline;
        a {
          color: #000000;
        }
      }
    }
    &-contents {
      padding: 26px 2rem;
    }
  }
}
</style>
<style lang="scss">
.overflow-y-hidden {
  .woo-tutorial-contents {
    position: absolute;
  }
}
</style>
